import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Phone, User as UserIcon, FileText, Download, Users as TeamIcon } from "lucide-react";
import Pill from "@/components/Pill";
import api from "@/lib/api";
import { useRefData } from "@/context/RefDataContext";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleString();
}
function initials(name = "") {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} text-zinc-200`}>
        {value || <span className="text-zinc-600">—</span>}
      </div>
    </div>
  );
}

/**
 * Read-only lightbox for a Task or Ticket.
 *
 * Props:
 *   open: { type: "task" | "ticket", id } | null
 *   onClose: () => void
 */
export default function TaskTicketLightbox({ open, onClose }) {
  const ref = useRefData();
  const [doc, setDoc] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let aborted = false;
    if (!open) {
      setDoc(null);
      setReplies([]);
      return;
    }
    setLoading(true);
    const base = open.type === "task" ? `/tasks/${open.id}` : `/issues/${open.id}`;
    Promise.all([api.get(base), api.get(`${base}/replies`).catch(() => ({ data: [] }))])
      .then(([d, r]) => {
        if (aborted) return;
        setDoc(d.data);
        setReplies(r.data || []);
      })
      .catch(() => { if (!aborted) setDoc(null); })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [open]);

  if (!open) return null;

  const isTask = open.type === "task";
  const projectLabel = ref.projects?.find((p) => p.value === doc?.project_id)?.label;
  const teamLabel = ref.teams?.find((t) => t.value === doc?.team_id)?.label;
  const assetLabel = ref.assets?.find((a) => a.value === doc?.asset_id)?.label;
  const serviceLabel = ref.services?.find((s) => s.value === doc?.service_id)?.label;
  const contacts = Array.isArray(doc?.contact_persons) ? doc.contact_persons : [];
  const files = Array.isArray(doc?.files) ? doc.files : [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-zinc-100 sm:max-w-3xl max-h-[88vh] overflow-y-auto"
        data-testid="task-ticket-lightbox"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`font-mono text-xs px-2 py-0.5 rounded ${
                isTask ? "bg-sky-500/15 text-sky-300 border border-sky-500/20" : "bg-amber-500/15 text-amber-300 border border-amber-500/20"
              }`}
              data-testid="lightbox-code"
            >
              {doc?.code || (isTask ? "Task" : "Ticket")}
            </span>
            <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">
              {isTask ? "Task" : "Ticket"} · Read-only
            </span>
          </div>
          <DialogTitle
            className="text-white text-xl font-semibold tracking-tight mt-2"
            style={{ fontFamily: "Outfit" }}
            data-testid="lightbox-subject"
          >
            {doc?.subject || (loading ? "Loading…" : "—")}
          </DialogTitle>
          {doc && (
            <DialogDescription className="text-zinc-500">
              <span className="flex items-center gap-3 flex-wrap mt-1">
                <Pill kind="priority" value={doc.priority} />
                <Pill kind="status" value={doc.status} />
                <span className="text-xs text-zinc-500">Created {fmtDate(doc.created_at)}</span>
              </span>
            </DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>
        ) : !doc ? (
          <div className="py-10 text-center text-red-400 text-sm">Failed to load {isTask ? "task" : "ticket"} details.</div>
        ) : (
          <div className="space-y-5 mt-2">
            {/* Description / Details body */}
            {(isTask ? doc.details : doc.description) && (
              <section className="bg-zinc-900 border border-white/10 rounded-lg p-4">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
                  {isTask ? "Task Details" : "Ticket Summary"}
                </div>
                <p className="text-sm text-zinc-200 whitespace-pre-wrap">{isTask ? doc.details : doc.description}</p>
              </section>
            )}

            {/* Field grid */}
            <section className="bg-zinc-900 border border-white/10 rounded-lg p-4">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Details</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                <Row label="CDARK" value={projectLabel} />
                <Row label="Team" value={teamLabel} />
                {isTask ? (
                  <>
                    <Row label="Due Date" value={doc.due_date} mono />
                    <Row label="Related Asset" value={assetLabel} mono />
                    <Row label="Related Service" value={serviceLabel} mono />
                  </>
                ) : (
                  <>
                    <Row label="Related Asset" value={assetLabel} mono />
                    <Row label="Related Service" value={serviceLabel} mono />
                    <Row label="Manager" value={doc.manager_name} />
                    <Row label="Manager Email" value={doc.manager_email} />
                    <Row label="Contact Number" value={doc.contact_number} mono />
                    <Row label="Extension" value={doc.extension_number} mono />
                  </>
                )}
              </div>
            </section>

            {/* Contacts (ticket-only) */}
            {!isTask && contacts.length > 0 && (
              <section className="bg-zinc-900 border border-white/10 rounded-lg p-4">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Contact Persons</div>
                <div className="space-y-2">
                  {contacts.map((c, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-4 text-sm">
                      <span className="flex items-center gap-1.5 text-zinc-200">
                        <UserIcon className="w-3.5 h-3.5 text-zinc-500" /> {c.name || "—"}
                      </span>
                      {c.email && (
                        <span className="flex items-center gap-1.5 text-zinc-300 text-xs">
                          <Mail className="w-3.5 h-3.5 text-zinc-500" /> {c.email}
                        </span>
                      )}
                      {c.phone && (
                        <span className="flex items-center gap-1.5 text-zinc-300 font-mono text-xs">
                          <Phone className="w-3.5 h-3.5 text-zinc-500" /> {c.phone}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Attachments */}
            {files.length > 0 && (
              <section className="bg-zinc-900 border border-white/10 rounded-lg p-4">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Attachments ({files.length})</div>
                <ul className="space-y-2">
                  {files.map((f) => (
                    <li key={f.id} className="flex items-center justify-between rounded-md bg-zinc-950 border border-white/5 px-3 py-2 text-sm">
                      <span className="flex items-center gap-2 text-zinc-200 truncate">
                        <FileText className="w-4 h-4 text-zinc-500" /> <span className="truncate">{f.name || f.filename}</span>
                      </span>
                      <a
                        href={`${api.defaults.baseURL}/files/${f.id}/download?auth=${encodeURIComponent(localStorage.getItem("ops_token") || "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-sky-400 hover:underline inline-flex items-center gap-1"
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Reply Timeline (read-only) */}
            <section data-testid="lightbox-timeline">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">
                Reply Timeline ({replies.length})
              </div>
              {replies.length === 0 ? (
                <div className="bg-zinc-900 border border-white/10 rounded-lg p-6 text-center text-sm text-zinc-500">
                  No replies yet.
                </div>
              ) : (
                <ol className="relative border-l border-white/10 ml-3 space-y-5">
                  {replies.map((r) => (
                    <li key={r.id} className="ml-6" data-testid={`lightbox-reply-${r.id}`}>
                      <span className="absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full bg-zinc-900 border border-white/10 text-[10px] font-medium text-white">
                        {initials(r.user_name || r.user_email)}
                      </span>
                      <div className="bg-zinc-900 border border-white/10 rounded-lg p-3">
                        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1.5">
                          <div className="text-xs">
                            <span className="font-medium text-white">{r.user_name || r.user_email}</span>
                          </div>
                          <div className="text-[11px] text-zinc-500" title={r.created_at}>
                            {timeAgo(r.created_at)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          {r.status_change ? (
                            <div className="text-[11px] text-zinc-400 flex items-center gap-2">
                              Status:
                              {r.previous_status && <Pill kind="status" value={r.previous_status} />}
                              <span className="text-zinc-500">→</span>
                              <Pill kind="status" value={r.status_change} />
                            </div>
                          ) : (
                            <Pill kind="status" value={r.current_status || doc.status} />
                          )}
                          {r.team_name && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-400 bg-zinc-950 border border-white/10 rounded-full px-2 py-0.5">
                              <TeamIcon className="w-3 h-3" /> {r.team_name}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-zinc-200 whitespace-pre-wrap">{r.message}</p>
                        {Array.isArray(r.files) && r.files.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {r.files.map((f) => (
                              <a
                                key={f.id}
                                href={`${api.defaults.baseURL}/files/${f.id}/download?auth=${encodeURIComponent(localStorage.getItem("ops_token") || "")}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-[11px] text-sky-400 hover:underline bg-zinc-950 border border-white/10 rounded px-1.5 py-0.5"
                              >
                                <FileText className="w-3 h-3" />
                                <span className="truncate max-w-[160px]">{f.filename || f.name}</span>
                                <Download className="w-3 h-3 opacity-60" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button
            onClick={onClose}
            className="bg-white text-black hover:bg-zinc-200 font-medium"
            data-testid="lightbox-close"
          >
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
